import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { InboxDto } from '@app/core/model';
import {
  AuthorizationApiService,
  DocumentReferenceApiService,
  GraphApiService,
  InboundFaxApiService,
  LookupApiService,
  LookupService,
  NotificationService,
  PlatformService,
} from '@app/core/service';
import { MoveFaxModalComponent } from '@app/features/move-fax-modal/move-fax-modal.component';
import {
  AddLineService,
  AttachToAuthorizationService,
  CreateNewAuthorizationService,
  FaxMemberService,
  FaxStatusService,
  FaxUserService,
  InboundFaxesService,
  PreviewFaxService,
  RequestTypeService,
} from '@app/pages/inbound-faxes/services';
import { TableLazyComponent } from '@app/shared/components';
import { GlobalToastComponent } from '@app/shared/components/global-toast/global-toast.component';
import { InboundFaxesTableModel, InboundFaxesTableModelKeys, TableColumnConfig } from '@app/shared/models';
import { AuthorizationNumberService, TableService } from '@app/shared/services';
import { DialogConfig } from '@app/shared/utility';
import { ConfirmationService, LazyLoadEvent, MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { DialogService, DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ToggleSwitchModule, ToggleSwitchChangeEvent } from 'primeng/toggleswitch';
import { MenuModule } from 'primeng/menu';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ArchiveFaxService } from './services/archive-fax.service';
import { InboundFaxesTableConfigService } from './services/inbound-faxes-table-config.service';
import { InboundFaxesTableQueryService } from './services/inbound-faxes-table-query.service';

@Component({
    selector: 'mnc-inbound-faxes',
    templateUrl: './inbound-faxes.component.html',
    styleUrls: ['./inbound-faxes.component.scss'],
    imports: [
        CommonModule,
        SelectModule,
        ButtonModule,
        MenuModule,
        FormsModule,
        ToggleSwitchModule,
        DynamicDialogModule,
        ConfirmDialogModule,
        ProgressSpinnerModule,
        GlobalToastComponent,
        ToastModule,
        DialogModule,
        TableLazyComponent,
    ],
    providers: [
        AuthorizationApiService,
        AuthorizationNumberService,
        InboundFaxesService,
        TableService,
        DialogService,
        ConfirmationService,
        DynamicDialogRef,
        InboundFaxApiService,
        RequestTypeService,
        PreviewFaxService,
        NotificationService,
        AddLineService,
        FaxStatusService,
        CreateNewAuthorizationService,
        FaxUserService,
        FaxMemberService,
        DocumentReferenceApiService,
        AttachToAuthorizationService,
        ArchiveFaxService,
        InboundFaxesTableConfigService,
        InboundFaxesTableQueryService,
        LookupService,
        LookupApiService,
        GraphApiService,
    ]
})
export class InboundFaxesComponent implements OnInit, OnDestroy {
  @Input() currentUserId: string;
  @Input() set configuration(value: string) {
    try {
      const { features } = JSON.parse(value);
      this.platformService.features = features;
    } catch {
      console.error('Invalid configuration passed');
    }
  }
  @ViewChild('inboundFaxesODataTable') inboundFaxesODataTable!: TableLazyComponent;

  get faxActionsDisabled(): boolean {
    return (
      !this.selectedRow?.memberSubscriberNumber || !this.selectedRow?.assignedUserId ||
      this.selectedRow.fileSizeBytes >= this.MAX_FILE_SIZE_BYTES ||
      this.showArchive ||
      this.faxActionMenuOptions.every(x => !x.visible)
    );
  }
  public readonly platformService = inject(PlatformService);
  private readonly inboundFaxesService = inject(InboundFaxesService);
  private readonly dialogService = inject(DialogService);
  private readonly requestTypeService = inject(RequestTypeService);
  private readonly notificationService = inject(NotificationService);
  private readonly previewFaxService = inject(PreviewFaxService);
  private readonly addLineService = inject(AddLineService);
  private readonly faxStatusService = inject(FaxStatusService);
  private readonly faxUserService = inject(FaxUserService);
  private readonly faxMemberService = inject(FaxMemberService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly createNewAuthService = inject(CreateNewAuthorizationService);
  private readonly attachToAuthorizationService = inject(AttachToAuthorizationService);
  private readonly archiveFaxService = inject(ArchiveFaxService);
  private readonly inboundFaxesTableConfigService = inject(InboundFaxesTableConfigService);
  private readonly inboundFaxesTableQueryService = inject(InboundFaxesTableQueryService);
  private readonly lookupService = inject(LookupService);

  public selectedInbox: InboxDto | null = null;
  public selectedRow: InboundFaxesTableModel | null = null;
  public columnConfig: TableColumnConfig[] = [];
  public tableData: InboundFaxesTableModel[] = [];
  public total: number;
  public faxActionMenuOptions: MenuItem[] = [];
  public showArchive = false;
  public inboxOptions: InboxDto[] = [];
  public isLoadingInboxes = true;
  public isLoadingTableData$ = this.inboundFaxesService.isLoadingTableData$;
  public defaultFaxesSort = {
    field: InboundFaxesTableModelKeys.faxReceivedDateTime,
    order: 1,
  };

  private readonly MAX_FILE_SIZE_BYTES = 20_000_000;

  // Utilized for saving inbox history in local storage
  private readonly INBOX_SELECTED_KEY = 'InboundFaxTable:InboxSelected';

  public async ngOnInit() {
    this.clearPersistedSelection();
    this.attachTableDataListener();

    this.inboxOptions = await this.lookupService.getInboxes();
    this.isLoadingInboxes = false;

    this.setInitialInbox();

    this.inboundFaxesTableConfigService.assignedUserClicked = (target: InboundFaxesTableModel) => {
      this.faxUserService.handleClick(target, this.selectedInbox, this.currentUserId);
    };

    this.inboundFaxesTableConfigService.assignedMemberClicked = (target: InboundFaxesTableModel) => {
      this.faxMemberService.handleClick(target, this.selectedInbox);
    };

    this.inboundFaxesTableConfigService.faxStatusClicked = (target: InboundFaxesTableModel) => {
      this.faxStatusService.handleClick(target, this.selectedInbox);
    };

    this.inboundFaxesTableConfigService.requestTypeClicked = (target: InboundFaxesTableModel) => {
      this.requestTypeService.handleClick(target, this.selectedInbox);
    };

    this.inboundFaxesTableConfigService.previewClicked = (target: InboundFaxesTableModel) => {
      this.previewFaxService.handleClick(target);
    };

    this.inboundFaxesTableConfigService.archiveFaxClicked = (target: InboundFaxesTableModel) => {
      this.archiveFaxService.handleArchiveClick(target, this.selectedInbox);
    };

    this.inboundFaxesTableConfigService.restoreArchiveFaxClicked = (target: InboundFaxesTableModel) => {
      this.archiveFaxService.handleRestoreArchiveClick(target, this.selectedInbox);
    };
  }

  public ngOnDestroy(): void {
    this.dialogService.dialogComponentRefMap.forEach(x => x.instance.close());

    // Without this, if the user clicks back while a modal is active, it locks the previous page
    // due to dismissableMask being set to false. We don't want to set that to true, as we don't
    // want the modal to close if user clicks outside the modal. Maybe if we bubbled up the dialogRef
    // to the shell is an option, but prefer the MFE to handle it to be self contained.
    document.querySelectorAll('p-dynamicdialog').forEach(mask => mask.remove());
  }

  public async onInboxChange(event: SelectChangeEvent): Promise<void> {
    this.isLoadingTableData$.next(true);
    this.showArchive = false;
    this.selectedInbox = event.value as InboxDto;
    localStorage.setItem(this.INBOX_SELECTED_KEY, (event.value as InboxDto).applicationKey);
    await this.loadTableConfig(true);
    this.inboundFaxesService.reload(this.selectedInbox, this.showArchive);
    this.loadFaxActions(event.value as InboxDto);
    this.selectedRow = null;
  }

  public async onArchiveChange(event: ToggleSwitchChangeEvent): Promise<void> {
    this.isLoadingTableData$.next(true);
    this.showArchive = event.checked;
    this.selectedRow = null;
    await this.loadTableConfig(true);
    this.inboundFaxesService.reload(this.selectedInbox, this.showArchive);

    if (this.showArchive) {
      this.notificationService.showMessage(
        'Archived Faxes',
        'After archiving, you can only edit the nonviable faxes but you need to restore them first.'
      );
    }
  }

  public resetTableState(): void {
    this.selectedRow = null;
    this.clearFiltersOnUI();
    this.inboundFaxesService.reload(this.selectedInbox, this.showArchive);
  }

  public handleRowClicked(row: InboundFaxesTableModel): void {
    if (!this.showArchive) {
      this.selectedRow = row;
    } else {
      this.selectedRow = null;
    }
  }

  public openMoveFaxModal(): void {
    this.dialogService.open(MoveFaxModalComponent, DialogConfig('Move Fax', {
      row: this.selectedRow,
      inbox: this.selectedInbox
    })).onClose.subscribe((res: string) => {
        if (res) {
          this.inboundFaxesService.reload(this.selectedInbox);
        }
      });
  }

  public reloadClicked(): void {
    this.selectedRow = null;
    this.inboundFaxesODataTable?.clearSelection();
    this.inboundFaxesService.reload(this.selectedInbox, this.showArchive);
  }

  public handleTableLazyLoad(event: LazyLoadEvent): void {
    if (this.selectedInbox && event) {
      this.inboundFaxesTableQueryService.updateQuery(this.selectedInbox, event);
      this.columnConfig = this.inboundFaxesTableConfigService.updateFilterValues(this.columnConfig, event);
    } else {
      this.inboundFaxesTableQueryService.initQuery(this.selectedInbox, this.showArchive);
    }
    this.inboundFaxesService.reload(this.selectedInbox, this.showArchive);
  }

  private async setInitialInbox(): Promise<void> {
    const inboxSelectedApplicationKey = localStorage.getItem(this.INBOX_SELECTED_KEY);

    if (inboxSelectedApplicationKey) {
      this.selectedInbox = this.inboxOptions.find((x) => x.applicationKey === inboxSelectedApplicationKey);

      if (this.selectedInbox) {
        //Check if user previously was viewing the archive page, so we want to clear filters.
        const oDataQuery = this.inboundFaxesTableQueryService.getQuery();
        if (oDataQuery?.isArchived) {
          this.inboundFaxesTableQueryService.resetArchive();
        }
        await this.loadTableConfig(false);
        this.loadFaxActions(this.selectedInbox);
      }
    } else {
      await this.loadTableConfig(false);
      this.isLoadingTableData$.next(false);
    }
  }

  private attachTableDataListener(): void {
    this.inboundFaxesService.tableData$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.tableData = data.items;
          this.total = data.count;
          if (this.selectedRow) {
            this.selectedRow = this.tableData.find(x => x.inboundFaxId === this.selectedRow.inboundFaxId) || null;
          }
        },
      });
  }

  private async loadTableConfig(clearFilters: boolean): Promise<void> {
    this.columnConfig = this.inboundFaxesTableConfigService.initTableConfig(this.showArchive, this.selectedInbox);

    if (this.selectedInbox) {
      this.columnConfig = await this.inboundFaxesTableConfigService.populateTableDropdowns(
        this.columnConfig,
        this.selectedInbox,
        this.currentUserId
      );
    }

    if (clearFilters) {
      this.clearFiltersOnUI();
    }
  }

  private loadFaxActions(inbox: InboxDto): void {
    const isVisible = this.platformService.getAvailableFaxActions(inbox);
    this.faxActionMenuOptions = [
      {
        label: 'Create New Authorization',
        visible: isVisible['CreateNewAuthorization'],
        command: () => this.createNewAuthService.handleClick(this.selectedRow, this.selectedInbox),
      },
      {
        label: 'Attach to Authorization',
        visible: isVisible['AttachToAuthorization'],
        command: () => this.attachToAuthorizationService.handleClick(this.selectedRow, this.selectedInbox),
      },
      {
        label: 'Add New Line',
        visible: isVisible['AddNewLine'],
        command: async () => await this.addLineService.handleClick(this.selectedRow, this.selectedInbox),
      },
    ];
  }

  private clearFiltersOnUI(): void {
    this.inboundFaxesTableQueryService.initQuery(this.selectedInbox, this.showArchive, true);
    this.columnConfig = this.inboundFaxesTableConfigService.clearFilters(this.columnConfig);
    this.inboundFaxesODataTable?.clearAllFilters(this.defaultFaxesSort.field, this.defaultFaxesSort.order);
  }

  private clearPersistedSelection(): void {
    const stateKey = 'InboundFaxes:Table:State';
    const savedState = localStorage.getItem(stateKey);
    
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        if (state.selection) {
          delete state.selection;
          localStorage.setItem(stateKey, JSON.stringify(state));
        }
      } catch (error) {
        console.error('Error clearing persisted selection:', error);
      }
    }
  }
}
